import { SortInput, SupportedSortBy } from '@apeiron/common'
import {
  PlanetCardInfoType,
  PlanetEmporiumSortItems,
  PlanetFilterInput,
  PlanetListMode,
  PlanetLite,
  PlanetNormalSortItems,
  PlanetSortBy
} from '@apeiron/planet'
import {
  ApeironTitle,
  DEFAULT_LIST_SIZE,
  Pagination,
  useAccountContext
} from '@apeiron/temp'
import { usePath } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import ListTemplate from '@src/components/layout/ListTemplate'
import PlanetFilter from '@src/components/planet/filter/PlanetFilter'
import { PlanetList } from '@src/components/planet/list/PlanetList'
import SortDropdown from '@src/components/share/generic/SortDropdown'
import { GAAssetType } from '@src/constants/googleAnalytics'
import { WhiteList } from '@src/constants/page'
import { ROUTE } from '@src/constants/route'
import { PlanetSkillsProvider } from '@src/contexts/planet/PlanetSkillsContext'
import { PlanetTypeProvider } from '@src/contexts/planet/PlanetTypeContext'
import { usePlanetSearchInputs } from '@src/hooks/apeiron/planet/usePlanetSearchInputs'
import useQueryPaging from '@src/hooks/common/router/useQueryPaging'
import useGetMyPlanets from '@src/hooks/graphql/useGetMyPlanets'
import useGetMySeedPlanets from '@src/hooks/graphql/useGetMySeedPlanets'
import useGetPlanets from '@src/hooks/graphql/useGetPlanets'
import {
  PlanetLiteDeserializeMode,
  PlanetLiteDeserializeOption
} from '@src/types/deserialize'
import queryString from 'query-string'
import * as R from 'ramda'
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

enum FetchMode {
  Public = 0,
  Private,
  Seed
}

const StyledPlanetFilter = styled(PlanetFilter)`
  overflow-y: auto;
  ${props => props.theme.breakpoints.up('res1024')} {
    position: sticky;
  }
`

const Total = styled(Typography)`
  font-size: 24px;
  line-height: 28px;
  color: white;
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 8px 20px;
  }
`

const ListContainer = styled(Box)`
  flex: 1;
  padding-top: 10px;
`

const PageContainer = styled(Box)`
  margin-top: 20px;
  width: 100%;
  height: 50px;
`

const StyledSortDropdown = styled(SortDropdown)`
  margin-right: 12px;
`

const HeaderLeftSection = styled(Box)`
  display: flex;
  flex-direction: column;
`

const PlanetListWithFilter: FC<Props> = (props: Props) => {
  const { t } = useTranslation()

  const { replacePath } = usePath(WhiteList)

  const {
    allowFetch = true,
    emptyMessage = t('planet.list.empty'),
    mode = PlanetListMode.TRADE,
    pageSize = DEFAULT_LIST_SIZE,
    thirdNavBar,
    title,
    disablePlanet,
    onItemClick,
    onPlanetStatusClick,
    onFetchSuccess,
    onAfterFilterChange
  } = props

  const { account, isLoggedIn } = useAccountContext()

  const filterHasChangeRef = useRef(false)

  const {
    loading: planetsLoading,
    fetch: fetchPlanets,
    data: planets,
    pageInfo: planetPage,
    called: planetsCalled
  } = useGetPlanets()

  const {
    loading: myPlanetsLoading,
    fetch: fetchMyPlanets,
    data: myPlanets,
    pageInfo: myPlanetPage,
    called: myPlanetsCalled
  } = useGetMyPlanets()

  const {
    loading: mySeedPlanetsLoading,
    fetch: fetchMySeedPlanets,
    data: mySeedPlanets,
    pageInfo: mySeedPlanetPage,
    called: mySeedPlanetsCalled
  } = useGetMySeedPlanets()

  const fetchMode = useMemo((): FetchMode => {
    if (
      mode === PlanetListMode.TRADE ||
      mode === PlanetListMode.OTHERS_INVENTORY
    ) {
      return FetchMode.Public
    }

    if (mode === PlanetListMode.SEED_INVENTORY) {
      return FetchMode.Seed
    }

    return FetchMode.Private
  }, [mode])

  const sortBy = useMemo(() => {
    switch (mode) {
      case PlanetListMode.INVENTORY:
      case PlanetListMode.OTHERS_INVENTORY:
      case PlanetListMode.FAVORITE:
        return PlanetSortBy.PlanetTypeAsc

      case PlanetListMode.TRADE:
        return PlanetSortBy.PriceAsc

      default:
        return null
    }
  }, [mode])

  const data = useMemo(() => {
    let planetsByMode = []

    switch (fetchMode) {
      case FetchMode.Public:
        planetsByMode = planets

        break

      case FetchMode.Seed:
        planetsByMode = mySeedPlanets

        break

      case FetchMode.Private:
      default:
        planetsByMode = myPlanets

        break
    }

    return disablePlanet
      ? R.map((planet: PlanetLite) => {
          const translationKey = disablePlanet(planet)

          return !R.isNil(translationKey) && !R.isEmpty(translationKey)
            ? {
                ...planet,
                disabled: true,
                bottomStatus: { translationKey }
              }
            : planet
        }, planetsByMode)
      : planetsByMode
  }, [disablePlanet, fetchMode, myPlanets, mySeedPlanets, planets])

  const loading = useMemo(() => {
    switch (fetchMode) {
      case FetchMode.Public:
        return planetsLoading

      case FetchMode.Seed:
        return mySeedPlanetsLoading

      case FetchMode.Private:
      default:
        return myPlanetsLoading
    }
  }, [fetchMode, myPlanetsLoading, mySeedPlanetsLoading, planetsLoading])

  const fetch = useMemo(() => {
    switch (fetchMode) {
      case FetchMode.Public:
        return fetchPlanets

      case FetchMode.Seed:
        return fetchMySeedPlanets

      case FetchMode.Private:
      default:
        return fetchMyPlanets
    }
  }, [fetchMode, fetchMyPlanets, fetchMySeedPlanets, fetchPlanets])

  const pageInfo = useMemo(() => {
    switch (fetchMode) {
      case FetchMode.Public:
        return planetPage

      case FetchMode.Seed:
        return mySeedPlanetPage

      case FetchMode.Private:
      default:
        return myPlanetPage
    }
  }, [fetchMode, myPlanetPage, mySeedPlanetPage, planetPage])

  const called = useMemo(() => {
    switch (fetchMode) {
      case FetchMode.Public:
        return planetsCalled

      case FetchMode.Seed:
        return mySeedPlanetsCalled

      case FetchMode.Private:
      default:
        return myPlanetsCalled
    }
  }, [fetchMode, myPlanetsCalled, mySeedPlanetsCalled, planetsCalled])

  const showFilter = mode !== PlanetListMode.SEED_INVENTORY

  const cardInfoType =
    mode === PlanetListMode.SEED_INVENTORY
      ? PlanetCardInfoType.Seed
      : PlanetCardInfoType.Conjunction

  const { queryPageNumber, setPageNumberQuery } = useQueryPaging()

  const {
    filterInput,
    sortInput,
    setSortInput,
    setFilterInput,
    setFilterAndSortInput
  } = usePlanetSearchInputs({
    defaultInput: { sortBy }
  })

  const doFetch = useCallback(
    async (params: Record<string, any>) => {
      let option: PlanetLiteDeserializeOption = {
        mode: PlanetLiteDeserializeMode.DEFAULT
      }

      switch (mode) {
        case PlanetListMode.INVENTORY:
        case PlanetListMode.FAVORITE: {
          option = {
            mode: PlanetLiteDeserializeMode.INVENTORY_AND_FAVORITE
          }

          break
        }

        case PlanetListMode.TRADE: {
          option = {
            mode: PlanetLiteDeserializeMode.EMPORIUM,
            walletAddress: account.walletAddress
          }
        }
      }

      const extraParams =
        mode === PlanetListMode.FAVORITE ? { bookmarked: true } : {}

      const requestInput = { ...params, ...extraParams } as Record<string, any>

      const result = await fetch({ ...params, ...extraParams }, option)

      if (onFetchSuccess) {
        onFetchSuccess(
          result.data,
          filterHasChangeRef.current,
          requestInput.filterInput
        )
      }

      filterHasChangeRef.current = false
    },
    [account.walletAddress, fetch, mode, onFetchSuccess]
  )

  const handleOnRenderUrl = useCallback(
    (planet: PlanetLite) => {
      if (mode === PlanetListMode.SEED_INVENTORY) {
        return ''
      }

      const url = queryString.stringifyUrl({
        url: ROUTE.MARKETPLACE_PLANET_DETAIL,
        query: {
          planetId: planet.planetID
        }
      })

      return replacePath(url)
    },
    [mode, replacePath]
  )

  const handleOnPreviousPage = useCallback(async () => {
    setPageNumberQuery(pageInfo.page - 1)
    doFetch({
      pageInput: {
        page: pageInfo.page - 1
      },
      filterInput,
      sortInput
    })
  }, [doFetch, filterInput, pageInfo.page, setPageNumberQuery, sortInput])

  const handleOnNextPage = useCallback(async () => {
    setPageNumberQuery(pageInfo.page + 1)
    doFetch({
      pageInput: {
        page: pageInfo.page + 1
      },
      filterInput,
      sortInput
    })
  }, [doFetch, filterInput, pageInfo.page, setPageNumberQuery, sortInput])

  const handleOnSubmitPage = useCallback(
    async (page: number) => {
      setPageNumberQuery(page)
      doFetch({
        pageInput: {
          page
        },
        filterInput,
        sortInput
      })
    },
    [doFetch, filterInput, setPageNumberQuery, sortInput]
  )

  const onSortChange = useCallback(
    (input: SortInput<SupportedSortBy>) => {
      setSortInput(input as SortInput<PlanetSortBy>)
    },
    [setSortInput]
  )

  const onFilterChange = useCallback(
    (input: PlanetFilterInput) => {
      setFilterInput(input)

      if (onAfterFilterChange) {
        onAfterFilterChange(GAAssetType.Planet, filterInput, input)
      }

      filterHasChangeRef.current = true
    },
    [setFilterInput, filterInput, onAfterFilterChange]
  )

  const handleOnFilterByFavorite = useCallback(
    (input: PlanetFilterInput, sort: SortInput<SupportedSortBy>) => {
      setFilterAndSortInput(input, sort as SortInput<PlanetSortBy>)

      filterHasChangeRef.current = true
    },
    [setFilterAndSortInput]
  )

  useEffect(() => {
    if (allowFetch) {
      doFetch({
        pageInput: { page: queryPageNumber },
        filterInput,
        sortInput
      })
    }
  }, [allowFetch, filterInput, mode, pageSize, queryPageNumber, sortInput])

  return (
    <PlanetTypeProvider>
      <PlanetSkillsProvider>
        <ListTemplate
          thirdNavBar={thirdNavBar}
          filter={
            showFilter ? (
              <StyledPlanetFilter
                filterInput={filterInput}
                isLoggedIn={isLoggedIn}
                onFilterInputChange={onFilterChange}
                onFilterByFavorite={handleOnFilterByFavorite}
                sortInput={sortInput}
              />
            ) : null
          }
          sortDropDown={
            showFilter ? (
              <StyledSortDropdown
                items={
                  mode === PlanetListMode.TRADE
                    ? PlanetEmporiumSortItems
                    : PlanetNormalSortItems
                }
                onSelectChange={onSortChange}
                value={sortInput}
              />
            ) : null
          }
          loadingContent={!called || loading}
          mainContent={
            <>
              <ListContainer>
                <PlanetList
                  cardInfoType={cardInfoType}
                  emptyRecommendation={mode === PlanetListMode.INVENTORY}
                  emptyStateDescription={emptyMessage || ''}
                  enableLink={mode !== PlanetListMode.SEED_INVENTORY}
                  planets={data}
                  onItemClick={onItemClick}
                  onPlanetStatusClick={onPlanetStatusClick}
                  renderPlanetUrl={handleOnRenderUrl}
                />
              </ListContainer>
              {pageInfo.totalPage > 0 && (
                <PageContainer>
                  <Pagination
                    prevDisabled={pageInfo.page === 1}
                    nextDisabled={R.isNil(pageInfo.nextCursor)}
                    onPrev={handleOnPreviousPage}
                    onNext={handleOnNextPage}
                    onSubmit={handleOnSubmitPage}
                    value={pageInfo.page}
                    totalPage={pageInfo.totalPage}
                  />
                </PageContainer>
              )}
            </>
          }
          headerLeft={
            <HeaderLeftSection>
              {title && <ApeironTitle text={title} />}
              {!loading && (
                <Total>
                  {t(
                    mode === PlanetListMode.SEED_INVENTORY
                      ? 'planet.list.seed.total'
                      : 'planet.list.nft.total',
                    { count: pageInfo.total }
                  )}
                </Total>
              )}
            </HeaderLeftSection>
          }
        />
      </PlanetSkillsProvider>
    </PlanetTypeProvider>
  )
}

type Props = {
  allowFetch?: boolean
  emptyMessage?: string
  mode?: PlanetListMode
  pageSize?: number
  thirdNavBar?: ReactNode
  title?: string
  disablePlanet?: (planet: PlanetLite) => string | null
  onItemClick?: (planet: PlanetLite) => void
  onPlanetStatusClick?: (planet: PlanetLite) => void
  onFetchSuccess?: (
    planets: PlanetLite[],
    filterHasChange: boolean,
    filterInput?: PlanetFilterInput
  ) => void
  onAfterFilterChange?: (
    type: GAAssetType,
    originalFilter: Record<string, any>,
    newFilter: Record<string, any>
  ) => void
}

export type { Props as PlanetListWithFilterProps }

export default PlanetListWithFilter
